import { useId } from 'react';

/**
 * Thumbnail + image URL field + styled file button (+ optional clear).
 */
export default function AdminImagePicker({
  previewUrl,
  fallbackEmoji = '🍽',
  urlValue,
  onUrlChange,
  onUrlBlur,
  onFileSelect,
  onClearImage,
}) {
  const inputId = useId();
  const hasPreview = Boolean(previewUrl);
  const showRemove = Boolean(hasPreview && onClearImage);

  const btnBase = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '.4rem',
    padding: '.55rem 1rem',
    fontSize: '.82rem',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    transition: 'background .15s, border-color .15s, color .15s',
    border: '1.5px solid',
    lineHeight: 1.2,
  };

  return (
    <div
      className="admin-image-picker"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '.85rem',
        alignItems: 'stretch',
      }}
    >
      <div
        style={{
          width: 88,
          height: 88,
          flexShrink: 0,
          borderRadius: 12,
          overflow: 'hidden',
          background: 'var(--cream)',
          border: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        {hasPreview ? (
          <img
            src={previewUrl}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <span style={{ fontSize: '2.25rem', lineHeight: 1 }} aria-hidden>
            {fallbackEmoji}
          </span>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0, width: '100%' }}>
        <label
          htmlFor={`${inputId}-url`}
          style={{
            display: 'block',
            fontSize: '.72rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '.06em',
            color: 'var(--gray)',
            marginBottom: 6,
          }}
        >
          Image URL
        </label>
        <input
          id={`${inputId}-url`}
          type="text"
          className="form-input"
          placeholder="https://…"
          inputMode="url"
          autoComplete="off"
          value={urlValue}
          onChange={(e) => onUrlChange(e.target.value)}
          onBlur={onUrlBlur}
          style={{
            width: '100%',
            padding: '.5rem .65rem',
            fontSize: '.85rem',
            marginBottom: 10,
            boxSizing: 'border-box',
          }}
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem', alignItems: 'center' }}>
          <input
            type="file"
            accept="image/*"
            id={`${inputId}-file`}
            style={{
              position: 'absolute',
              width: 1,
              height: 1,
              padding: 0,
              margin: -1,
              overflow: 'hidden',
              clip: 'rect(0,0,0,0)',
              whiteSpace: 'nowrap',
              border: 0,
            }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              onFileSelect(f || null);
              e.target.value = '';
            }}
          />
          <label
            htmlFor={`${inputId}-file`}
            style={{
              ...btnBase,
              background: 'var(--teal-pale)',
              borderColor: 'rgba(26, 122, 138, 0.35)',
              color: 'var(--teal-dark)',
            }}
          >
            Choose image
          </label>
          {showRemove && (
            <button
              type="button"
              onClick={onClearImage}
              style={{
                ...btnBase,
                background: 'rgba(226, 75, 75, 0.08)',
                borderColor: 'rgba(200, 72, 72, 0.4)',
                color: 'var(--red)',
              }}
            >
              Remove image
            </button>
          )}
        </div>
        <p style={{ fontSize: '.72rem', color: 'var(--gray)', margin: '8px 0 0', lineHeight: 1.45 }}>
          Paste a URL or choose a file from your device. The thumbnail updates when an image is set.
        </p>
      </div>
      <style>{`
        @media (min-width: 480px) {
          .admin-image-picker {
            flex-direction: row !important;
            align-items: flex-start !important;
            gap: 1.15rem !important;
          }
        }
      `}</style>
    </div>
  );
}
